// web/src/components/lessons/LessonEditor.jsx
// Modal to create or edit one lesson for a given rider + day. Owns its own
// mutations; invalidates ['lessons'] + ['schedule'] on success and closes.
//
// The rider comes from the cell you tapped in the rider grid. The Schedule
// board has no rider axis, so when `riderPersonId` is omitted the form grows a
// rider picker instead — same editor, both entry points.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import TimeSelect from '../ui/TimeSelect';
import { colorClass } from '../../lib/lessonTypes';
import { fmtDate } from '../../lib/dates';

// Types are barn config now (Settings → Lesson Types), not a list baked in
// here. The old hard-coded array lived in two components and a SQL comment and
// couldn't be changed without a deploy.
const STATUSES = [
  ['scheduled', 'Scheduled'],
  ['tentative', 'Tentative / TBD'],
  ['completed', 'Completed'],
  ['cancelled', 'Canceled'],
];

const DEFAULT_TIME = '07:00'; // new lessons start here

export default function LessonEditor({
  lesson,
  date,
  riderPersonId,
  riderName,
  riders = [],
  horses = [],
  onClose,
}) {
  const editing = Boolean(lesson);
  const qc = useQueryClient();

  // Fixed when the caller knows the rider (grid cell); picked in-form otherwise.
  const riderFixed = Boolean(riderPersonId);
  const [riderId, setRiderId] = useState(riderPersonId ?? lesson?.riderPersonId ?? '');

  // Seed from the lesson, or default to 7:00 AM for a new one. A lesson with no
  // time opens as TBD. TimeSelect owns the 12-hour split; this keeps the 24-hour
  // string the API wants.
  const [tbd, setTbd] = useState(editing ? !lesson?.startTime : false);
  const [time, setTime] = useState(lesson?.startTime || DEFAULT_TIME);

  const [horseId, setHorseId] = useState(lesson?.horseId ?? '');
  const [lessonTypeId, setLessonTypeId] = useState(lesson?.lessonTypeId ?? '');

  // Only active types are offered. A lesson already on a retired type keeps it
  // — the select just won't list it, which is the point of retiring rather
  // than deleting.
  const typesQ = useQuery({
    queryKey: ['lesson-types'],
    queryFn: () => api.get('/lesson-types'),
  });
  const lessonTypes = typesQ.data?.lessonTypes ?? [];
  const selectedType = lessonTypes.find((t) => t.id === lessonTypeId) ?? null;
  const [status, setStatus] = useState(lesson?.status ?? 'scheduled');
  const [notes, setNotes] = useState(lesson?.notes ?? '');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['lessons'] });
    qc.invalidateQueries({ queryKey: ['schedule'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        date,
        riderPersonId: riderId,
        startTime: tbd ? null : time,
        horseId: horseId || null,
        lessonTypeId: lessonTypeId || null,
        status,
        notes: notes.trim() || null,
      };
      return editing ? api.patch(`/lessons/${lesson.id}`, payload) : api.post('/lessons', payload);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const del = useMutation({
    mutationFn: () => api.del(`/lessons/${lesson.id}`),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 sm:p-4"
      onClick={onClose}
    >
      <div
        className="card w-full sm:max-w-md p-5 rounded-b-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold">{editing ? 'Edit Lesson' : 'Add Lesson'}</h2>
          <p className="text-sm text-ink-500">
            {riderName ? `${riderName} · ` : ''}
            {formatDate(date)}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!save.isPending && riderId) save.mutate();
          }}
          className="space-y-4"
        >
          {!riderFixed && (
            <Field label="Rider" htmlFor="rider">
              <select
                id="rider"
                className="input w-full"
                value={riderId}
                onChange={(e) => setRiderId(e.target.value)}
              >
                <option value="">Pick a rider…</option>
                {riders.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Time" optional>
            <TimeSelect label="Lesson time" value={time} onChange={setTime} disabled={tbd} />
            <label className="mt-2 inline-flex items-center gap-2 text-sm text-ink-500">
              <input type="checkbox" checked={tbd} onChange={(e) => setTbd(e.target.checked)} />
              No set time (TBD)
            </label>
          </Field>

          <Field label="Horse" optional htmlFor="horse">
            <select
              id="horse"
              className="input w-full"
              value={horseId}
              onChange={(e) => setHorseId(e.target.value)}
            >
              <option value="">—</option>
              {horses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                  {h.stall ? ` (stall ${h.stall})` : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Type" optional htmlFor="type">
            <select
              id="type"
              className="input w-full"
              value={lessonTypeId}
              onChange={(e) => setLessonTypeId(e.target.value)}
            >
              <option value="">—</option>
              {lessonTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {selectedType && (
              <p className="mt-1 text-xs text-ink-400 flex items-center gap-1.5">
                <span className={'w-2 h-2 rounded-full ' + colorClass(selectedType.color, 'dot')} />
                {selectedType.defaultMinutes} min ·{' '}
                {selectedType.defaultCapacity > 1
                  ? `group of ${selectedType.defaultCapacity}`
                  : 'private'}
              </p>
            )}
          </Field>

          <Field label="Status" htmlFor="status">
            <select
              id="status"
              className="input w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map(([v, lbl]) => (
                <option key={v} value={v}>
                  {lbl}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Notes" optional htmlFor="notes">
            <textarea
              id="notes"
              rows={2}
              className="input w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          {(save.isError || del.isError) && (
            <p className="text-sm text-red-600">Something went wrong. Try again.</p>
          )}

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-3">
              <button type="submit" className="btn-primary" disabled={save.isPending || !riderId}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={onClose} className="text-sm text-ink-500 hover:text-ink-800">
                Cancel
              </button>
            </div>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Delete this lesson?')) del.mutate();
                }}
                disabled={del.isPending}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-700 mb-1">
        {label}
        {optional && <span className="font-normal text-ink-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

// "HH:MM" (24h) → { hour: "1".."12", minute: "MM", ampm: "AM"|"PM" }, or null.
function formatDate(iso) {
  if (!iso) return '';
  return fmtDate(iso, 'dayMedium');
}
